/**
 * EmployeeSignaturesListPage — iter-218c (Feb 2026)
 *
 * Lists every saved employee signature (from `admin_employee_signatures`).
 * "إضافة توقيع لموظف" opens a form (`EmployeeSignatureFormPage`) that
 * picks a user from the admin platform, snapshots their AR/EN name + job
 * title, and stores their signature image.
 *
 * Route: /control-panel-settings/company-mgmt/employees/signature
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PenLine, Plus, Loader2, User as UserIcon, Pencil, Trash2, CheckCircle2,
} from 'lucide-react';

import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmployeeSignaturesListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const isPrimaryAdmin = user?.email === 'admin@asb606.com' || user?.username === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/certificates/employee-signatures`, {
        headers: getAuthHeaders(),
      });
      setRows(r.data?.data || []);
    } catch {
      toast.error(t('فشل تحميل التوقيعات', 'Failed to load signatures'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (uid, name) => {
    if (!window.confirm(t(
      `حذف توقيع "${name}" نهائياً؟`,
      `Permanently delete "${name}"'s signature?`,
    ))) return;
    try {
      await axios.delete(`${API}/api/admin/certificates/employee-signatures/${uid}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <DashboardLayout
      title="توقيعات الموظفين"
      titleEn="Employee Signatures"
      subtitle="إدارة توقيعات الموظفين — يُستخدم توقيع المُعتمِد عند اعتماد الشهادات"
      subtitleEn="Manage employee signatures — the approver's own signature is used when approving certificates"
      screenCode="EMP-SIG"
      backPath="/control-panel-settings/company-mgmt/employees"
      onBack={() => navigate('/control-panel-settings/company-mgmt/employees')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'إعدادات الموظفين', labelEn: 'Employees Settings', path: '/control-panel-settings/company-mgmt/employees' },
        { labelAr: 'التوقيع', labelEn: 'Signatures' },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="employee-signatures-list-page">
        {/* Header row: title + add btn */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/40 flex items-center justify-center">
              <PenLine className="w-4 h-4 text-purple-300" />
            </div>
            <div>
              <div className="text-sm text-white/50">{t('التوقيعات المحفوظة', 'Saved signatures')}</div>
              <div className="text-lg font-bold text-white">{rows.length}</div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/control-panel-settings/company-mgmt/employees/signature/new')}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="add-employee-signature-btn"
          >
            <Plus className="w-4 h-4 me-1.5" />
            {t('إضافة توقيع لموظف', 'Add employee signature')}
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-14 text-center text-white/40"
               data-testid="employee-signatures-empty">
            <UserIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm mb-4">
              {t('لا توجد توقيعات محفوظة بعد — أضف أول توقيع لموظف.',
                 'No signatures saved yet — add the first one.')}
            </p>
          </div>
        ) : (
          <motion.div
            initial="hidden" animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {rows.map((row) => (
              <motion.div
                key={row.user_id}
                variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } }}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition"
                data-testid={`emp-sig-row-${row.user_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-500/15 border border-purple-500/40 flex items-center justify-center shrink-0">
                    <UserIcon className="w-5 h-5 text-purple-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white truncate">
                        {isAr ? (row.employee_name_ar || row.employee_name_en) : (row.employee_name_en || row.employee_name_ar)}
                      </h3>
                      {row.has_signature && (
                        <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          {t('محفوظ', 'Saved')}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/60 space-y-0.5">
                      {(row.job_title_ar || row.job_title_en) && (
                        <div>
                          <span className="text-white/40">{t('المسمى', 'Title')}:</span>{' '}
                          {isAr ? (row.job_title_ar || row.job_title_en) : (row.job_title_en || row.job_title_ar)}
                        </div>
                      )}
                      {row.user_email && (
                        <div dir="ltr">
                          <span className="text-white/40">{t('البريد', 'Email')}:</span>{' '}
                          {row.user_email}
                        </div>
                      )}
                      {row.updated_at && (
                        <div className="text-white/30 text-[10px]">
                          {t('آخر تحديث', 'Last updated')}: {new Date(row.updated_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-GB')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  <Button
                    onClick={() => navigate(`/control-panel-settings/company-mgmt/employees/signature/${row.user_id}`)}
                    variant="outline"
                    size="sm"
                    className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06] h-8"
                    data-testid={`emp-sig-edit-${row.user_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 me-1.5" />
                    {t('تعديل', 'Edit')}
                  </Button>
                  {isPrimaryAdmin && (
                    <Button
                      onClick={() => remove(row.user_id, isAr ? row.employee_name_ar : row.employee_name_en)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/40 bg-red-500/[0.06] text-red-200 hover:bg-red-500/[0.12] h-8"
                      data-testid={`emp-sig-delete-${row.user_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </section>
    </DashboardLayout>
  );
}
